import Button from "@/components/bricks/button/Button";
import { useConfirm } from "@/components/confirm/useConfirm";
import ChevronLeftIcon from "@/components/icons/ChevronLeftIcon";
import DeleteIcon from "@/components/icons/DeleteIcon";
import DownloadIcon from "@/components/icons/DownloadIcon";
import Spinner from "@/components/spinner/Spinner";
import { UserQueryContext } from "@/context/UserContext";
import { useDownloadInjunctionDecision } from "@/hooks/cle-hooks";
import {
  useDeletePersonPaabud,
  usePersonPaabud,
} from "@/hooks/person-hooks/usePersonPaabud";
import { formatLessons } from "@/util/number";
import { useContext, useMemo, type ReactNode } from "react";

import type { DecoratedPersonEfteruddannelse } from "./decoratePeriod";
import { EditPaabudModal } from "./EditPaabudModal";
import { findTransferredLinjer } from "./injunctionLessonTypeUtils";
import { PaabudDocumentAccordions } from "./PaabudDocumentAccordions";
import { PaabudLessonTypeSummaryCard } from "./PaabudLessonTypeSummaryCard";
import {
  mapIndberetningStatusBadgeVariant,
  mapIndberetningStatusLabel,
} from "./paabudUtils";
import { StatusBadge } from "./StatusBadge";
import {
  formaterFrist,
  formaterIndfrielsesperiode,
  formaterResterendeTid,
} from "./injunctionPeriode";
import { formatDate, formatOverfoertTilPeriode } from "./utils/formatters";

interface PaabudDetailViewProps {
  personId: string;
  paabudId: string;
  allPeriods: DecoratedPersonEfteruddannelse[];
  onBack: () => void;
  onSelectCertificate: (documentId: string) => void;
  onSelectReduction: (reductionId: string) => void;
  onSelectLegalAid: (legalAidId: string) => void;
  onSelectAuthorship: (authorshipId: string) => void;
  onSelectTeaching: (teachingId: string) => void;
}

const SummaryField = ({
  label,
  value,
  className,
}: {
  label: string;
  value: ReactNode;
  className?: string;
}) => (
  <div className={className ?? "col-xl-3 col-md-6"}>
    <div className="h-100 rounded border bg-light p-3">
      <div className="text-muted small mb-1">{label}</div>
      <div className="fw-semibold fs-6">{value}</div>
    </div>
  </div>
);

export const PaabudDetailView = ({
  personId,
  paabudId,
  allPeriods,
  onBack,
  onSelectCertificate,
  onSelectReduction,
  onSelectLegalAid,
  onSelectAuthorship,
  onSelectTeaching,
}: PaabudDetailViewProps) => {
  const { user, hasPermission } = useContext(UserQueryContext);
  const canWrite = hasPermission(user, "TilsynWrite");
  const { confirmDialog } = useConfirm();
  const { data, isLoading, error } = usePersonPaabud({
    personId,
    paabudId,
  });
  const transferredLinjer = useMemo(
    () =>
      findTransferredLinjer(
        allPeriods,
        data?.indberetning?.overfoertTilPersonEfteruddannelseIndberetningId,
      ),
    [allPeriods, data],
  );
  const { mutate: downloadInjunctionDecision, isPending: isDownloading } =
    useDownloadInjunctionDecision();
  const { mutate: deletePaabud, isPending: isDeleting } =
    useDeletePersonPaabud();

  const handleDownloadClick = () => {
    downloadInjunctionDecision({
      userId: personId,
      injunctionId: paabudId,
    });
  };

  const handleDelete = async () => {
    const confirmed = await confirmDialog(
      "Slet påbud",
      "Er du sikker på, at du vil slette dette påbud? Handlingen kan ikke fortrydes.",
      "Annuller",
      "Slet",
    );

    if (!confirmed) return;

    deletePaabud(
      { personId, paabudId },
      {
        onSuccess: () => onBack(),
      },
    );
  };

  if (isLoading) {
    return (
      <div className="container">
        <Spinner label="Henter påbud ..." />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="container">
        <Button
          id="efteruddannelse-paabud-back"
          color="secondary"
          transparent
          type="button"
          className="ps-0 mb-3"
          onClick={onBack}
        >
          <ChevronLeftIcon />
          Tilbage til efteruddannelse
        </Button>
        <div className="text-danger">Kunne ikke hente påbud.</div>
      </div>
    );
  }

  const title =
    data.decisionDocumentFileName ??
    `${formatLessons(data.paakraevedeLektioner)} lektioner`;
  const indberetningStatus =
    data.indberetning?.efteruddannelseIndberetningStatus;
  const indberetningStatusLabel =
    mapIndberetningStatusLabel(indberetningStatus);
  const hasBemaerkning = Boolean(data.bemaerkning?.trim());

  return (
    <div className="container pb-4">
      <div className="mb-3">
        <Button
          id="efteruddannelse-paabud-back"
          color="secondary"
          transparent
          type="button"
          className="ps-0"
          onClick={onBack}
        >
          <ChevronLeftIcon />
          Tilbage til efteruddannelse
        </Button>
      </div>
      <div className="c-card p-4 mb-4">
        <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-4">
          <div>
            <div className="text-muted small mb-1">Påbud</div>
            <h2 className="h3 mb-0">{title}</h2>
          </div>

          <div className="d-flex flex-wrap gap-2 align-items-center flex-shrink-0">
            {canWrite && (
              <>
                <EditPaabudModal personId={personId} paabudId={paabudId} />
                {data.status !== "Slettet" && (
                  <Button
                    color="secondary"
                    disabled={isDeleting || isDownloading}
                    id="efteruddannelse-paabud-delete"
                    type="button"
                    onClick={handleDelete}
                  >
                    {isDeleting ? (
                      <Spinner size={14} />
                    ) : (
                      <DeleteIcon height={16} />
                    )}
                    Slet
                  </Button>
                )}
              </>
            )}
            <Button
              color="-action"
              disabled={isDownloading || isDeleting}
              id="efteruddannelse-paabud-download"
              type="button"
              onClick={handleDownloadClick}
            >
              {isDownloading ? (
                <Spinner size={14} />
              ) : (
                <DownloadIcon height={16} />
              )}
              Hent afgørelse
            </Button>
          </div>
        </div>

        <div className="row g-3">
          <SummaryField
            label="Afgørelsesdato"
            value={formatDate(data.kendelseDato)}
          />
          <SummaryField
            label="Dokumentation gælder fra"
            value={formatDate(data.dokumentationGaelderFra ?? data.periodeFra)}
          />
          <SummaryField
            label="Indfrielsesperiode"
            value={formaterIndfrielsesperiode(
              data.periodeFra,
              data.periodeTil,
              formatDate,
            )}
          />
          <SummaryField
            label="Indfrielsesperiodens længde"
            value={
              data.periodeLaengdeMaaneder
                ? `${data.periodeLaengdeMaaneder} måneder`
                : "-"
            }
          />
          <SummaryField
            label="Påkrævede lektioner"
            value={formatLessons(data.paakraevedeLektioner)}
          />
          <SummaryField
            label="Gennemførte lektioner"
            value={formatLessons(data.lektionerGennemfoert)}
          />
          <SummaryField
            label="Overførte lektioner"
            value={formatLessons(
              data.lektionerOverfoert ?? data.indberetning?.lektionerOverfoert,
            )}
          />

          <SummaryField
            label="Dokumentationsfrist"
            value={formaterFrist(
              data.indberetning?.indberetningFrist,
              formatDate,
            )}
          />
          {data.erIBero ? (
            <SummaryField
              label="I bero"
              value={
                data.iBeroSiden
                  ? `Siden ${formatDate(data.iBeroSiden)} — genoptages ved genudlevering`
                  : "Afventer at advokaten er udøvende længe nok"
              }
            />
          ) : (
            <SummaryField
              label="Resterende tid"
              value={formaterResterendeTid(data.resterendeUdoevendeDage)}
            />
          )}
          <SummaryField
            label="Modtaget dato"
            value={formatDate(data.indberetning?.registreringsDato)}
          />
          {data.indberetning?.signaturNavn ? (
            <SummaryField
              label="Indberetning signeret af"
              value={data.indberetning.signaturNavn}
            />
          ) : null}
          <SummaryField
            label="Status"
            value={
              indberetningStatus ? (
                <StatusBadge
                  pill
                  status={indberetningStatusLabel}
                  variant={mapIndberetningStatusBadgeVariant(
                    indberetningStatus,
                  )}
                />
              ) : (
                "-"
              )
            }
          />
          <SummaryField label="Oprettet af" value={data.opretInit ?? "-"} />
          {data.indberetning?.overfoertTilPersonEfteruddannelseId && (
            <SummaryField
              label="Overført til periode"
              value={formatOverfoertTilPeriode(
                data.indberetning.overfoertTilPeriodeFra,
                data.indberetning.overfoertTilPeriodeTil,
                data.indberetning.overfoertTilPersonEfteruddannelseId,
              )}
            />
          )}
          {hasBemaerkning && (
            <SummaryField
              className="col-12"
              label="Bemærkning"
              value={data.bemaerkning ?? "-"}
            />
          )}
        </div>
      </div>
      {data.indberetning?.linjer?.length ? (
        <section className="mb-4">
          <PaabudLessonTypeSummaryCard
            linjer={data.indberetning?.linjer}
            transferredLinjer={transferredLinjer}
          />
        </section>
      ) : null}
      <section className="mb-4">
        <h3 className="h5 mb-3">Indberettede lektionsdokumenter</h3>
        <PaabudDocumentAccordions
          paabudId={paabudId}
          userId={personId}
          onSelectCertificate={onSelectCertificate}
          onSelectReduction={onSelectReduction}
          onSelectLegalAid={onSelectLegalAid}
          onSelectAuthorship={onSelectAuthorship}
          onSelectTeaching={onSelectTeaching}
        />
      </section>
    </div>
  );
};
